import { ArrowRightLeft } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import {
  PolicyKind,
  type Policy,
  type PolicyKind as PolicyKindValue,
  type PolicyStatus,
} from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { MediaRichTextEditor } from '@/features/media';
import { slugify } from '@/features/products';
import {
  resolveSeoPreview,
  SeoOverrideFields,
  SeoPreviewCard,
  stripHtml,
  truncateWords,
  useSeoStoreContext,
} from '@/features/seo';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  POLICY_SUMMARY_MAX,
  policyFormSchema,
  type PolicyFormInput,
  type PolicyFormValues,
} from '../schemas/policy-form-schema';
import { defaultPolicyHandle, policyPath } from '../schemas/policy-rules';

export const POLICY_FORM_ID = 'policy-form';
const STATUS_ORDER: PolicyStatus[] = ['draft', 'published'];

export interface PolicyFormProps {
  storeId: string;
  /** The saved policy; null while creating. */
  policy: Policy | null;
  defaultValues: PolicyFormInput;
  /** Kinds a new document may still take; every kind while editing (the select is disabled). */
  availableKinds?: readonly PolicyKindValue[];
  isSubmitting: boolean;
  onSubmit: (values: PolicyFormValues) => Promise<unknown>;
  dangerZone?: ReactNode;
}

function LivePreview({ storeId, policy }: { storeId: string; policy: Policy | null }) {
  const context = useSeoStoreContext(storeId);
  const [title, handle, summary, bodyHtml, status, seo] = useWatch<
    PolicyFormInput,
    ['title', 'handle', 'summary', 'bodyHtml', 'status', 'seo']
  >({ name: ['title', 'handle', 'summary', 'bodyHtml', 'status', 'seo'] });
  const path = policyPath(handle.trim() ? handle : (policy?.handle ?? ''));
  const description = summary.trim()
    ? summary
    : bodyHtml.trim()
      ? truncateWords(stripHtml(bodyHtml), 300)
      : null;
  const live = context
    ? resolveSeoPreview(
        {
          title,
          description,
          path,
          published: status === 'published',
          override: seo,
          image: null,
        },
        context,
      )
    : null;
  return <SeoPreviewCard live={live} saved={policy?.resolvedSeo ?? null} path={path} />;
}

/**
 * Kind, title, address, summary, body and the SEO override in the main column; status, save and
 * the live search preview in the side rail, bound to the same form. The kind is fixed once saved.
 */
export function PolicyForm({
  storeId,
  policy,
  defaultValues,
  availableKinds = Object.values(PolicyKind),
  isSubmitting,
  onSubmit,
  dangerZone,
}: PolicyFormProps) {
  const { t } = useTranslation(['policies', 'common']);
  const message = useErrorMessage();
  const form = useForm<PolicyFormInput, unknown, PolicyFormValues>({
    resolver: useI18nResolver(policyFormSchema),
    defaultValues,
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const [handleTouched, setHandleTouched] = useState(policy !== null);
  const kind = form.watch('kind');
  const handle = form.watch('handle');
  const summary = form.watch('summary');
  const renamed = policy !== null && handle.trim() !== '' && handle.trim() !== policy.handle;
  const isDirty = form.formState.isDirty;
  const rootError = form.formState.errors.root?.server?.message;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await onSubmit(values);
      if (policy) form.reset(values);
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  useSaveBar({ form, onSave: submit, saving: isSubmitting });

  return (
    <Form {...form}>
      <FormLayout
        aside={
          <>
            <FormSection title={t('policies:sections.status')} tour="settings.policy-editor.status">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="gap-2.5"
                        aria-label={t('policies:sections.status')}
                      >
                        {STATUS_ORDER.map((status) => (
                          <div key={status} className="flex items-start gap-2.5">
                            <RadioGroupItem
                              value={status}
                              id={`policy-status-${status}`}
                              className="mt-0.5"
                            />
                            <div className="grid gap-0.5">
                              <Label htmlFor={`policy-status-${status}`}>
                                {t(`policies:status.${status}`)}
                              </Label>
                              <span className="text-muted-foreground text-xs">
                                {t(`policies:statusHint.${status}`)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Can permission="content.write">
                <div className="flex items-center gap-2 border-t pt-4">
                  <Button
                    type="submit"
                    form={POLICY_FORM_ID}
                    disabled={isSubmitting || (policy !== null && !isDirty)}
                    className="flex-1"
                    data-tour="settings.policy-editor.save"
                  >
                    {isSubmitting
                      ? t('common:actions.saving')
                      : policy
                        ? t('common:actions.save')
                        : t('policies:create.submit')}
                  </Button>
                  {policy ? (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={isSubmitting || !isDirty}
                      onClick={() => form.reset()}
                    >
                      {t('policies:actions.discard')}
                    </Button>
                  ) : null}
                </div>
              </Can>
            </FormSection>
            <LivePreview storeId={storeId} policy={policy} />
            {dangerZone}
          </>
        }
      >
        <form id={POLICY_FORM_ID} onSubmit={submit} noValidate className="flex flex-col gap-6">
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          <FormSection
            tour="settings.policy-editor.details"
            title={t('policies:sections.details')}
            description={t('policies:sections.detailsDescription')}
          >
            <FormField
              control={form.control}
              name="kind"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('policies:fields.kind')}</FormLabel>
                  <Select
                    value={field.value}
                    disabled={policy !== null}
                    onValueChange={(value) => {
                      field.onChange(value);
                      if (!handleTouched) {
                        const next = value as PolicyKindValue;
                        form.setValue(
                          'handle',
                          next === 'custom'
                            ? slugify(form.getValues('title'))
                            : defaultPolicyHandle(next),
                          { shouldDirty: true },
                        );
                      }
                    }}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full sm:w-72">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(PolicyKind).map((option) => (
                        <SelectItem
                          key={option}
                          value={option}
                          disabled={option !== field.value && !availableKinds.includes(option)}
                        >
                          {t(`policies:kind.${option}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {policy
                      ? t('policies:fields.kindFixed')
                      : t(`policies:kindHint.${field.value}`)}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('policies:fields.title')}</FormLabel>
                  <FormControl>
                    <Input
                      autoFocus={policy === null}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        if (!handleTouched && kind === 'custom') {
                          form.setValue('handle', slugify(e.target.value), { shouldDirty: true });
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="handle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('policies:fields.handle')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="font-mono text-[13px]"
                      onChange={(e) => {
                        setHandleTouched(true);
                        field.onChange(e);
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    {t('policies:fields.handleHint', { path: policyPath(field.value || 'handle') })}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {renamed ? (
              <Alert role="status" data-testid="handle-rename-hint">
                <ArrowRightLeft aria-hidden />
                <AlertDescription>
                  {policy.status === 'published'
                    ? t('policies:fields.renameHint', {
                        from: policyPath(policy.handle),
                        to: policyPath(handle.trim()),
                      })
                    : t('policies:fields.renameDraftHint', {
                        from: policyPath(policy.handle),
                        to: policyPath(handle.trim()),
                      })}
                </AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="summary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('policies:fields.summary')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormDescription className="flex justify-between gap-2">
                    <span>{t('policies:fields.summaryHint')}</span>
                    <span
                      className={
                        summary.trim().length > POLICY_SUMMARY_MAX ? 'text-destructive' : undefined
                      }
                    >
                      {t('policies:fields.counter', {
                        length: summary.trim().length,
                        max: POLICY_SUMMARY_MAX,
                      })}
                    </span>
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <FormSection
            tour="settings.policy-editor.body"
            title={t('policies:sections.body')}
            description={t('policies:sections.bodyDescription')}
          >
            <FormField
              control={form.control}
              name="bodyHtml"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('policies:fields.bodyHtml')}</FormLabel>
                  <FormControl>
                    <MediaRichTextEditor
                      value={field.value}
                      onChange={(html) => field.onChange(html ?? '')}
                      onBlur={field.onBlur}
                      aria-label={t('policies:fields.bodyHtml')}
                    />
                  </FormControl>
                  <FormDescription>{t('policies:fields.bodyHtmlHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <SeoOverrideFields />
        </form>
      </FormLayout>
    </Form>
  );
}
